import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InsertLinkRoundedIcon from '@mui/icons-material/InsertLinkRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import { Fade, MenuItem, Paper, Popper, Skeleton } from '@mui/material';
import actions from '@src/redux/actions';
import CrownImg from '@src/assets/images/crown.png';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useUserInfo from '@src/hooks/useTtsUser';
import routes from '@src/constants/route';
import { validateUserPackageCanUseVoiceCloning } from '@src/services/voiceCloning';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import { checkFeaturePermission } from '@src/services/tts';
import CommonButton from '@src/new-components/Button';
import { useCanUseEOLVoice } from '@src/hooks/useCanUseEOLVoice';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import {
  FEATURE,
  PACK_DURATION,
  PACKAGE_LEVEL,
  PACKAGE_LEVEL_V3,
  PACKAGE_TYPE,
} from '@src/constants/package';
import apis from '@src/apis';
import { useMappingPackage } from '@src/hooks/useMappingPackage';
import { checkVietNam } from '@src/utils/checkCountry';
import {
  FAVOURITE_VOICES_ACTION,
  FAVOURITE_VOICES_LIMIT,
} from '@src/constants/voiceLibrary';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import { useHistory } from 'react-router-dom';
import { COLOR } from '@src/styles/color';
import { useTranslation } from 'react-i18next';
import { CRM_URL, LANDING_PAGE_URL } from '@src/configs';
import { VOICE_CODE, VOICE_PROVIDER } from '@src/constants/voice';
import {
  SUB_STATUS,
  VOICE_PROFILE_PAGE_STATUS,
  VOICE_STATUS,
} from '@src/constants/voiceCloning';
import { StyledActionsVoice } from '../index.style';

const MENU_ITEM_STYLE = {
  color: '#1E164E',
  fontSize: '14px',
  height: '40px',
  boxSizing: 'border-box',
  padding: '10px 16px',
  borderRadius: '8px',
  fontWeight: 400,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const BUTTON_ICON_STYLE = {
  width: '24px',
  height: '24px',
};

const BUTTON_STYLE = {
  borderRadius: '8px',
  height: '40px',
};

const ICON_BUTTON_STYLE = {
  ...BUTTON_STYLE,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '40px',
  width: '40px',
};

const ActionsVoice = ({
  voice,
  style,
  isLoading,
  isFavourite,
  onFavoriteToggleRequest,
  onHandleFavoriteProcessingComplete,
  onErrorProcessingFavouriteVoices,
  setFavouriteVoiceCodes,
}) => {
  const dispatch = useDispatch();
  const { getFeatureValue } = useFeatureFlags();
  const history = useHistory();
  const { t } = useTranslation();
  const { ttsUser } = useUserInfo();
  const { usingDubbingPackage, usingPackage } = useSelector(
    (state) => state.user,
  );
  const { handleGetPackageCodeMapping } = useMappingPackage();
  const { defaultGlobalVoice } = useSelector((state) => state.voice);
  const { user } = useSelector((state) => state.auth);
  const [anchorBlockOption, setAnchorBlockOption] = useState(null);
  const [openPopperBlockOption, setOpenPopperBlockOption] = useState(false);
  const [isFavorite, setIsFavorite] = useState(isFavourite);
  const { canUseEOLVoice } = useCanUseEOLVoice();
  const userInFeatureVoiceCloning = getFeatureValue(
    FEATURE_KEYS.VOICE_CLONING,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const refClickOutSide = useClickOutside(() =>
    setOpenPopperBlockOption(false),
  );

  const notify = (message, severity) => {
    dispatch(actions.noti.push({ severity, message }));
  };

  const handleClickCopy = (content, message) => {
    if (!content) return;
    navigator.clipboard.writeText(content);
    notify(message, 'success');
  };

  const getCopyLink = () => {
    const { status, slug } = voice.profilePage || {};
    if (status === VOICE_PROFILE_PAGE_STATUS.PUBLIC) {
      const slugProfile = slug ?? voice.code;
      return `${LANDING_PAGE_URL}/voices/${slugProfile}`;
    }
    return `${CRM_URL}${routes.VOICE_LIBRARY}?${VOICE_CODE}=${voice.code}`;
  };

  const hiddenCopyLink = voice?.status !== VOICE_STATUS.PUBLIC;

  const handleMenuItemClick = (callback) => (e) => {
    e.stopPropagation();
    callback();
    setAnchorBlockOption(null);
    setOpenPopperBlockOption(false);
  };

  const renderPopoverMenuItem = ({ onClick, label, icon }) => (
    <MenuItem
      className="option_filter"
      style={MENU_ITEM_STYLE}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#1E164E14';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '';
      }}
      onClick={onClick}
    >
      {icon}
      {label}
    </MenuItem>
  );

  const canUseVoiceCloning = useMemo(
    () =>
      validateUserPackageCanUseVoiceCloning({
        ttsUser,
        voice,
        isUserInWhiteList: userInFeatureVoiceCloning,
      }),
    [ttsUser, voice, userInFeatureVoiceCloning],
  );

  const isVietNam = checkVietNam();
  const isDefaultGlobalVoice =
    !isVietNam &&
    usingPackage?.level === PACKAGE_LEVEL.BASIC &&
    voice?.code === defaultGlobalVoice;

  const canUseVoice = () => {
    const isVoiceCloning =
      voice?.provider === VOICE_PROVIDER.VBEE_VOICE_CLONING &&
      voice?.userId === ttsUser?.id &&
      voice?.status === SUB_STATUS.PRIVATE;

    if (isVoiceCloning && !canUseVoiceCloning) return false;

    if (!voice?.features?.length) return true;

    if (!usingDubbingPackage && isDefaultGlobalVoice) return true;

    const eolVoice = voice?.eolDate;
    if (eolVoice && canUseEOLVoice({ voice, user: ttsUser })) return true;

    return voice.features.every((item) =>
      checkFeaturePermission(ttsUser?.features, item),
    );
  };

  const handleLimitedFeatureVoiceDialog = () => {
    const isBasicGlobalVoice = voice?.features?.includes(
      FEATURE.BASIC_GLOBAL_VOICE,
    );
    const isAdvancedGlobalVoice = voice?.features?.includes(
      FEATURE.ADVANCED_GLOBAL_VOICE,
    );

    if (isAdvancedGlobalVoice) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('voiceUpgradeVip'),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: 'sm',
          onUpgrade: () => {
            const upgradePackage = handleGetPackageCodeMapping({
              level: PACKAGE_LEVEL_V3.VIP,
              type: PACKAGE_TYPE.STUDIO,
              expiresIn: PACK_DURATION.YEAR,
            });
            history.push(`${routes.PAYMENT}?package=${upgradePackage}`);
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );

      return;
    }

    if (isBasicGlobalVoice) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('voiceUpgradeSpecialOrVip'),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: 'sm',
          onUpgrade: () => {
            history.push(routes.PAYMENT);
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );
      return;
    }

    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('limitedFeatureDesc', { feature: t('useThisVoice') }),
      }),
    );
  };

  const handleUseVoice = (e) => {
    dispatch(actions.layout.openHighlightWeekDialog(false));
    e.stopPropagation();
    if (!canUseVoice()) {
      handleLimitedFeatureVoiceDialog();
      return;
    }

    dispatch(actions.synthesisRequest.updateVoice(voice));
    history.push(routes.TTS);
  };

  const handleUpdateFavouriteVoices = async ({ voiceCode, action }) => {
    const data = await apis.favouriteVoices.updateFavouriteVoices({
      voiceCode,
      action,
    });
    if (data?.status) {
      onHandleFavoriteProcessingComplete({ voice });
    } else {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
      onErrorProcessingFavouriteVoices(voiceCode);
      throw new Error();
    }
  };

  const handleClickFavorite = async (e) => {
    e.stopPropagation();
    const result = onFavoriteToggleRequest(voice?.code);

    if (!result.allowed) {
      if (result.reason === 'limit') {
        notify(
          t('maxFavouriteVoices', {
            numFavouriteVoices: FAVOURITE_VOICES_LIMIT,
          }),
          'warning',
        );
      }
      return;
    }
    handleUpdateFavouriteVoices({
      voiceCode: voice?.code,
      action: isFavorite
        ? FAVOURITE_VOICES_ACTION.REMOVE
        : FAVOURITE_VOICES_ACTION.ADD,
    });
    if (isFavorite) {
      setFavouriteVoiceCodes((prev) =>
        prev.filter((code) => code !== voice?.code),
      );
    } else {
      setFavouriteVoiceCodes((prev) => [...prev, voice?.code]);
    }
    setIsFavorite((prev) => !prev);
  };

  return (
    <StyledActionsVoice marginTop={style?.marginTopActions}>
      {isLoading ? (
        <>
          {[...Array(3)].map(() => (
            <Skeleton
              style={{ borderRadius: '8px' }}
              variant="rectangular"
              animation="wave"
              width={32}
              height={32}
            />
          ))}
        </>
      ) : (
        voice?.code && (
          <>
            <CommonButton
              customStyle={BUTTON_STYLE}
              variant="outlineSmall"
              textProps={{
                variantText: 'body-14-medium',
                color: COLOR.darkBlue,
              }}
              onClick={handleUseVoice}
            >
              {t('voiceUse')}
            </CommonButton>
            <CommonButton
              customStyle={ICON_BUTTON_STYLE}
              variant="outlineSmall"
              onClick={handleClickFavorite}
            >
              {isFavorite ? (
                <FavoriteRoundedIcon
                  sx={{
                    ...BUTTON_ICON_STYLE,
                    color: COLOR.indigo.default,
                  }}
                />
              ) : (
                <FavoriteBorderRoundedIcon
                  sx={{
                    width: '24px',
                    height: '24px',
                    color: COLOR.indigo[80],
                  }}
                />
              )}
            </CommonButton>
            <div ref={refClickOutSide}>
              <CommonButton
                customStyle={ICON_BUTTON_STYLE}
                variant="outlineSmall"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenPopperBlockOption((prev) => !prev);
                  setAnchorBlockOption(e.currentTarget);
                }}
              >
                <MoreVertRoundedIcon
                  sx={{ ...BUTTON_ICON_STYLE, color: COLOR.indigo[80] }}
                />
              </CommonButton>
              <Popper
                transition
                style={{ zIndex: 9999, color: '#1E164E' }}
                anchorEl={anchorBlockOption}
                open={openPopperBlockOption}
                placement="bottom-end"
                modifiers={[
                  {
                    name: 'offset',
                    options: { offset: [0, 8] },
                  },
                ]}
              >
                {({ TransitionProps }) => (
                  <Fade
                    style={{
                      padding: '8px',
                      boxShadow: '0px 0px 20px 2px rgba(0, 0, 0, 0.1)',
                      borderRadius: '8px',
                    }}
                    {...TransitionProps}
                    timeout={350}
                  >
                    <Paper>
                      {renderPopoverMenuItem({
                        onClick: handleMenuItemClick(() =>
                          handleClickCopy(voice.code, t('copiedVoiceCode')),
                        ),
                        label: t('copyVoiceCode'),
                        icon: <ContentCopyRoundedIcon />,
                      })}
                      {!hiddenCopyLink &&
                        renderPopoverMenuItem({
                          onClick: handleMenuItemClick(() =>
                            handleClickCopy(
                              getCopyLink(),
                              t('copiedLinkVoice'),
                            ),
                          ),
                          label: t('copyLinkVoice'),
                          icon: <InsertLinkRoundedIcon />,
                        })}
                    </Paper>
                  </Fade>
                )}
              </Popper>
            </div>
          </>
        )
      )}
    </StyledActionsVoice>
  );
};

export default ActionsVoice;
